/**
 * Material Breakdown Chart Component
 * Displays stacked bar chart showing diversion vs landfill for each material type
 * Uses simple CSS-based visualization without external charting libraries
 */

import { formatWeight } from '../../utils/formatting';
import type { MaterialSummary } from '../../types/job';

interface MaterialBreakdownChartProps {
  summary: MaterialSummary[];
}

const MATERIAL_COLORS: Record<string, string> = {
  Concrete: 'bg-slate-500',
  Asphalt: 'bg-gray-700',
  Wood: 'bg-amber-600',
  Metal: 'bg-zinc-400',
  Drywall: 'bg-blue-300',
  Cardboard: 'bg-orange-400',
  'Green Waste': 'bg-green-600',
  'Mixed Waste': 'bg-purple-500',
  Other: 'bg-gray-400',
};

export function MaterialBreakdownChart({ summary }: MaterialBreakdownChartProps) {
  if (!summary || summary.length === 0) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">
          Materials Breakdown
        </h3>
        <p className="text-neutral-500 text-center py-8">
          No materials recorded yet
        </p>
      </div>
    );
  }

  const totalWeight = summary.reduce((sum, m) => sum + m.total_weight, 0);

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-neutral-900 mb-6">
        Materials Breakdown
      </h3>

      <div className="space-y-6">
        {/* Material bars */}
        {summary
          .sort((a, b) => b.total_weight - a.total_weight)
          .map((material) => {
            const percentage = (material.total_weight / totalWeight) * 100;
            const colorClass =
              MATERIAL_COLORS[material.material_type] || 'bg-gray-400';
            const divertedPercent =
              (material.diverted_weight / material.total_weight) * 100;
            const landfillPercent =
              (material.landfill_weight / material.total_weight) * 100;

            return (
              <div key={material.material_type} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium text-neutral-900">
                    {material.material_type}
                  </span>
                  <div className="flex gap-3 text-neutral-600">
                    <span>{formatWeight(material.total_weight)}</span>
                    <span className="text-neutral-500">
                      ({percentage.toFixed(0)}%)
                    </span>
                  </div>
                </div>

                {/* Stacked bar showing diverted vs landfill */}
                <div className="relative h-8 bg-neutral-100 rounded-lg overflow-hidden">
                  {/* Diverted portion - full opacity */}
                  <div
                    className={`absolute h-full ${colorClass}`}
                    style={{
                      width: `${divertedPercent}%`,
                    }}
                  />
                  {/* Landfill portion - reduced opacity */}
                  <div
                    className={`absolute h-full ${colorClass} opacity-40`}
                    style={{
                      left: `${divertedPercent}%`,
                      width: `${landfillPercent}%`,
                    }}
                  />
                </div>

                {/* Weight breakdown */}
                <div className="flex justify-between text-xs text-neutral-600">
                  <div className="flex items-center gap-1">
                    <svg
                      className="w-4 h-4 text-accent-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>
                      {formatWeight(material.diverted_weight)} diverted
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <svg
                      className="w-4 h-4 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    <span>
                      {formatWeight(material.landfill_weight)} landfill
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

        {/* Legend */}
        <div className="pt-4 border-t border-neutral-200">
          <div className="flex items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-neutral-700 rounded" />
              <span className="text-neutral-600">Diverted (full color)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-neutral-700 opacity-40 rounded" />
              <span className="text-neutral-600">Landfill (faded)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
